/**
 * RSD Inventory - Interactive Features
 * Provides filtering, searching, sorting, and view toggling for the inventory table
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRSDInventory);
  } else {
    initRSDInventory();
  }

  function initRSDInventory() {
    // Check if inventory data exists
    if (!window.RSDInventory || !Array.isArray(window.RSDInventory)) {
      console.warn('RSD Inventory: No data found');
      return;
    }

    const inventory = window.RSDInventory;
    const PICKS_STORAGE_KEY = 'rsd-2026-picks';
    
    // DOM Elements
    const elements = {
      searchInput: document.getElementById('rsd-search'),
      clearSearch: document.getElementById('rsd-clear-search'),
      formatFilter: document.getElementById('format-filter'),
      locationFilters: document.getElementById('location-filters'),
      inStockOnly: document.getElementById('in-stock-only'),
      resultsCount: document.getElementById('results-count'),
      clearFilters: document.getElementById('clear-filters'),
      tableBody: document.getElementById('rsd-table-body'),
      table: document.getElementById('rsd-table'),
      tableWrapper: document.querySelector('.rsd-table-wrapper'),
      cardsContainer: document.getElementById('rsd-cards'),
      noResults: document.getElementById('no-results'),
      viewTable: document.getElementById('view-table'),
      viewCards: document.getElementById('view-cards'),
      sortableHeaders: document.querySelectorAll('.rsd-th--sortable'),
      myPicksBtn: document.getElementById('my-picks-btn'),
      picksCount: document.getElementById('picks-count')
    };

    // Picks (favorites) persistence
    const picks = loadPicks();

    // Current state
    const state = {
      searchQuery: '',
      format: 'all',
      location: 'all',
      inStockOnly: false,
      showPicksOnly: false,
      sortBy: null,
      sortDirection: 'asc',
      viewMode: 'table',
      filteredData: [...inventory]
    };

    // Initialize
    renderTable(inventory);
    updateResultsCount(inventory.length, inventory.length);
    updatePicksCount();
    bindEvents();

    /**
     * Bind all event listeners
     */
    function bindEvents() {
      // Search input
      if (elements.searchInput) {
        elements.searchInput.addEventListener('input', debounce(handleSearch, 200));
        elements.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            clearSearch();
          }
        });
      }

      // Clear search button
      if (elements.clearSearch) {
        elements.clearSearch.addEventListener('click', clearSearch);
      }

      // Format filter dropdown
      if (elements.formatFilter) {
        elements.formatFilter.addEventListener('change', (e) => {
          state.format = e.target.value;
          applyFilters();
        });
      }

      // Location filter buttons
      if (elements.locationFilters) {
        elements.locationFilters.addEventListener('click', (e) => {
          const btn = e.target.closest('.rsd-filter-btn');
          if (!btn) return;
          
          // Update active state
          elements.locationFilters.querySelectorAll('.rsd-filter-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          
          state.location = btn.dataset.location;
          applyFilters();
        });
      }

      // In-stock only checkbox
      if (elements.inStockOnly) {
        elements.inStockOnly.addEventListener('change', (e) => {
          state.inStockOnly = e.target.checked;
          applyFilters();
        });
      }

      // Clear all filters
      if (elements.clearFilters) {
        elements.clearFilters.addEventListener('click', resetFilters);
      }

      // Sortable headers
      elements.sortableHeaders.forEach(header => {
        header.addEventListener('click', () => {
          const sortKey = header.dataset.sort;
          handleSort(sortKey, header);
        });
      });

      // View toggle buttons
      if (elements.viewTable) {
        elements.viewTable.addEventListener('click', () => setViewMode('table'));
      }
      if (elements.viewCards) {
        elements.viewCards.addEventListener('click', () => setViewMode('cards'));
      }

      // My Picks toggle
      if (elements.myPicksBtn) {
        elements.myPicksBtn.addEventListener('click', () => {
          state.showPicksOnly = !state.showPicksOnly;
          elements.myPicksBtn.setAttribute('aria-pressed', state.showPicksOnly);
          if (state.showPicksOnly) {
            elements.myPicksBtn.style.backgroundColor = '#e63946';
            elements.myPicksBtn.style.borderColor = '#e63946';
          } else {
            elements.myPicksBtn.style.backgroundColor = '#1a1a2e';
            elements.myPicksBtn.style.borderColor = '#1a1a2e';
          }
          applyFilters();
        });
        elements.myPicksBtn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            elements.myPicksBtn.click();
          }
        });
      }

      // Heart click delegation on table and cards
      if (elements.tableBody) {
        elements.tableBody.addEventListener('click', (e) => {
          const btn = e.target.closest('.rsd-heart-btn');
          if (btn) handleHeartClick(btn);
        });
      }
      if (elements.cardsContainer) {
        elements.cardsContainer.addEventListener('click', (e) => {
          const btn = e.target.closest('.rsd-heart-btn');
          if (btn) handleHeartClick(btn);
        });
      }
    }

    /**
     * Handle search input
     */
    function handleSearch(e) {
      const query = e.target.value.trim().toLowerCase();
      state.searchQuery = query;
      
      // Show/hide clear button
      if (elements.clearSearch) {
        elements.clearSearch.hidden = query.length === 0;
      }
      
      applyFilters();
    }

    /**
     * Clear search input
     */
    function clearSearch() {
      if (elements.searchInput) {
        elements.searchInput.value = '';
        state.searchQuery = '';
        if (elements.clearSearch) {
          elements.clearSearch.hidden = true;
        }
        applyFilters();
        elements.searchInput.focus();
      }
    }

    /**
     * Apply all active filters
     */
    function applyFilters() {
      let filtered = [...inventory];

      // Search filter
      if (state.searchQuery) {
        const query = state.searchQuery;
        filtered = filtered.filter(item => 
          item.title.toLowerCase().includes(query) ||
          item.artist.toLowerCase().includes(query)
        );
      }

      // Format filter
      if (state.format !== 'all') {
        filtered = filtered.filter(item => {
          if (!item.format) return false;
          const format = item.format.toLowerCase();
          const filterValue = state.format.toLowerCase();
          
          // Special handling for multi-LP formats
          if (filterValue === '3 x lp') {
            return format.includes('3 x lp') || format.includes('4 x lp') || format.includes('5 x lp');
          }
          
          // Special handling for picture disc
          if (filterValue === 'picture disc') {
            return format.includes('picture disc');
          }
          
          return format.includes(filterValue);
        });
      }

      // Location filter
      if (state.location !== 'all') {
        const stockKey = state.location.toLowerCase() + 'Stock';
        filtered = filtered.filter(item => item[stockKey] > 0);
      }

      // In-stock only filter
      if (state.inStockOnly) {
        filtered = filtered.filter(item => 
          item.southStock > 0 || item.westStock > 0 || item.ankStock > 0
        );
      }

      // My Picks filter
      if (state.showPicksOnly) {
        filtered = filtered.filter(item => picks.has(getPickKey(item)));
      }

      // Apply sorting
      if (state.sortBy) {
        filtered = sortData(filtered, state.sortBy, state.sortDirection);
      }

      state.filteredData = filtered;

      // Update UI
      renderTable(filtered);
      renderCards(filtered);
      updateResultsCount(filtered.length, inventory.length);
      updateClearFiltersVisibility();
      updateNoResultsVisibility(filtered.length);
    }

    /**
     * Handle column sorting
     */
    function handleSort(sortKey, header) {
      // Toggle direction if same column
      if (state.sortBy === sortKey) {
        state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortBy = sortKey;
        state.sortDirection = 'asc';
      }

      // Update header visual states
      elements.sortableHeaders.forEach(h => {
        h.classList.remove('sort-asc', 'sort-desc');
      });
      header.classList.add(state.sortDirection === 'asc' ? 'sort-asc' : 'sort-desc');

      applyFilters();
    }

    /**
     * Sort data by key
     */
    function sortData(data, key, direction) {
      return [...data].sort((a, b) => {
        let valA = a[key] || '';
        let valB = b[key] || '';

        // String comparison
        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    /**
     * Reset all filters to default
     */
    function resetFilters() {
      // Reset state
      state.searchQuery = '';
      state.format = 'all';
      state.location = 'all';
      state.inStockOnly = false;
      state.showPicksOnly = false;
      state.sortBy = null;
      state.sortDirection = 'asc';

      // Reset UI elements
      if (elements.searchInput) {
        elements.searchInput.value = '';
      }
      if (elements.clearSearch) {
        elements.clearSearch.hidden = true;
      }
      if (elements.formatFilter) {
        elements.formatFilter.value = 'all';
      }
      if (elements.inStockOnly) {
        elements.inStockOnly.checked = false;
      }
      if (elements.myPicksBtn) {
        elements.myPicksBtn.setAttribute('aria-pressed', 'false');
        elements.myPicksBtn.style.backgroundColor = '#1a1a2e';
        elements.myPicksBtn.style.borderColor = '#1a1a2e';
      }

      // Reset filter buttons
      document.querySelectorAll('.rsd-filter-btn').forEach(btn => {
        const isAll = btn.dataset.filter === 'all' || btn.dataset.location === 'all';
        btn.classList.toggle('active', isAll);
      });

      // Reset sort headers
      elements.sortableHeaders.forEach(h => {
        h.classList.remove('sort-asc', 'sort-desc');
      });

      applyFilters();
    }

    /**
     * Set view mode (table or cards)
     */
    function setViewMode(mode) {
      state.viewMode = mode;

      if (elements.viewTable) {
        elements.viewTable.classList.toggle('active', mode === 'table');
      }
      if (elements.viewCards) {
        elements.viewCards.classList.toggle('active', mode === 'cards');
      }

      if (elements.tableWrapper) {
        elements.tableWrapper.hidden = mode !== 'table';
      }
      if (elements.cardsContainer) {
        elements.cardsContainer.hidden = mode !== 'cards';
      }

      // Render cards if switching to card view
      if (mode === 'cards') {
        renderCards(state.filteredData);
      }
    }

    /**
     * Render table rows
     */
    function renderTable(data) {
      if (!elements.tableBody) return;

      if (data.length === 0) {
        elements.tableBody.innerHTML = '';
        return;
      }

      const html = data.map(item => {
        const key = getPickKey(item);
        const picked = picks.has(key);

        return `
          <tr class="rsd-row${picked ? ' rsd-row--picked' : ''}">
            <td class="rsd-td rsd-td--heart">
              <button type="button" class="rsd-heart-btn${picked ? ' active' : ''}" data-pick-key="${escapeAttr(key)}" aria-label="${picked ? 'Remove from' : 'Add to'} My Picks" aria-pressed="${picked}">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="${picked ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </button>
            </td>
            <td class="rsd-td">
              <span class="rsd-title">${escapeHtml(item.title)}</span>
              <span class="rsd-artist">${escapeHtml(item.artist)}</span>
            </td>
            <td class="rsd-td rsd-td--hide-mobile">${escapeHtml(item.artist)}</td>
            <td class="rsd-td rsd-td--hide-mobile">
              <span class="rsd-format">${escapeHtml(item.format || 'N/A')}</span>
            </td>
            <td class="rsd-td rsd-td--center">
              ${renderStockBadge(item.southStock)}
            </td>
            <td class="rsd-td rsd-td--center">
              ${renderStockBadge(item.westStock)}
            </td>
            <td class="rsd-td rsd-td--center">
              ${renderStockBadge(item.ankStock)}
            </td>
          </tr>
        `;
      }).join('');

      elements.tableBody.innerHTML = html;
    }

    /**
     * Render card view
     */
    function renderCards(data) {
      if (!elements.cardsContainer) return;

      if (data.length === 0) {
        elements.cardsContainer.innerHTML = '';
        return;
      }

      const html = data.map(item => {
        const key = getPickKey(item);
        const picked = picks.has(key);

        return `
          <div class="rsd-card${picked ? ' rsd-card--picked' : ''}">
            <div class="rsd-card__header">
              <div>
                <h3 class="rsd-card__title">${escapeHtml(item.title)}</h3>
                <p class="rsd-card__artist">${escapeHtml(item.artist)}</p>
              </div>
              <button type="button" class="rsd-heart-btn rsd-heart-btn--card${picked ? ' active' : ''}" data-pick-key="${escapeAttr(key)}" aria-label="${picked ? 'Remove from' : 'Add to'} My Picks" aria-pressed="${picked}">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="${picked ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </button>
            </div>
            <div class="rsd-card__badges">
              <span class="rsd-format">${escapeHtml(item.format || 'N/A')}</span>
            </div>
            <div class="rsd-card__stock">
              <div class="rsd-card__stock-item">
                <span class="rsd-card__stock-label">SOUTH</span>
                <span class="rsd-card__stock-value ${item.southStock > 0 ? 'rsd-card__stock-value--available' : 'rsd-card__stock-value--out'}">${item.southStock}</span>
              </div>
              <div class="rsd-card__stock-item">
                <span class="rsd-card__stock-label">WEST</span>
                <span class="rsd-card__stock-value ${item.westStock > 0 ? 'rsd-card__stock-value--available' : 'rsd-card__stock-value--out'}">${item.westStock}</span>
              </div>
              <div class="rsd-card__stock-item">
                <span class="rsd-card__stock-label">ANK</span>
                <span class="rsd-card__stock-value ${item.ankStock > 0 ? 'rsd-card__stock-value--available' : 'rsd-card__stock-value--out'}">${item.ankStock}</span>
              </div>
            </div>
          </div>
        `;
      }).join('');

      elements.cardsContainer.innerHTML = html;
    }

    /**
     * Render stock badge
     */
    function renderStockBadge(count) {
      const num = parseInt(count) || 0;
      let className = 'rsd-stock--out';
      
      if (num > 2) {
        className = 'rsd-stock--available';
      } else if (num > 0) {
        className = 'rsd-stock--low';
      }

      return `<span class="rsd-stock ${className}">${num}</span>`;
    }

    /**
     * Update results count display
     */
    function updateResultsCount(showing, total) {
      if (!elements.resultsCount) return;
      
      if (showing === total) {
        elements.resultsCount.textContent = `Showing all ${total} releases`;
      } else {
        elements.resultsCount.textContent = `Showing ${showing} of ${total} releases`;
      }
    }

    /**
     * Update clear filters button visibility
     */
    function updateClearFiltersVisibility() {
      if (!elements.clearFilters) return;
      
      const hasActiveFilters = 
        state.searchQuery !== '' ||
        state.format !== 'all' ||
        state.location !== 'all' ||
        state.inStockOnly ||
        state.showPicksOnly;
      
      elements.clearFilters.hidden = !hasActiveFilters;
    }

    /**
     * Update no results message visibility
     */
    function updateNoResultsVisibility(count) {
      if (!elements.noResults) return;
      
      elements.noResults.hidden = count > 0;
      
      if (elements.tableWrapper) {
        elements.tableWrapper.hidden = count === 0 || state.viewMode !== 'table';
      }
      if (elements.cardsContainer) {
        elements.cardsContainer.hidden = count === 0 || state.viewMode !== 'cards';
      }
    }

    /**
     * Generate a unique key for a pick based on title + artist
     */
    function getPickKey(item) {
      return (item.title || '') + '|||' + (item.artist || '');
    }

    /**
     * Load picks from localStorage
     */
    function loadPicks() {
      try {
        const stored = localStorage.getItem(PICKS_STORAGE_KEY);
        return stored ? new Set(JSON.parse(stored)) : new Set();
      } catch (e) {
        return new Set();
      }
    }

    /**
     * Save picks to localStorage
     */
    function savePicks() {
      try {
        localStorage.setItem(PICKS_STORAGE_KEY, JSON.stringify([...picks]));
      } catch (e) {
        // localStorage full or unavailable
      }
    }

    /**
     * Handle heart button click
     */
    function handleHeartClick(btn) {
      const key = btn.dataset.pickKey;
      if (!key) return;

      const isPicked = picks.has(key);
      if (isPicked) {
        picks.delete(key);
      } else {
        picks.add(key);
      }

      savePicks();
      updatePicksCount();

      // If viewing picks only and we un-picked something, re-filter
      if (state.showPicksOnly) {
        applyFilters();
      } else {
        // Just update the visual state of all matching buttons without full re-render
        document.querySelectorAll(`.rsd-heart-btn[data-pick-key="${CSS.escape(key)}"]`).forEach(heartBtn => {
          const nowPicked = picks.has(key);
          heartBtn.classList.toggle('active', nowPicked);
          heartBtn.setAttribute('aria-pressed', nowPicked);
          heartBtn.setAttribute('aria-label', (nowPicked ? 'Remove from' : 'Add to') + ' My Picks');
          const svg = heartBtn.querySelector('svg');
          if (svg) svg.setAttribute('fill', nowPicked ? 'currentColor' : 'none');
        });
        // Update row/card picked class
        document.querySelectorAll(`.rsd-heart-btn[data-pick-key="${CSS.escape(key)}"]`).forEach(heartBtn => {
          const row = heartBtn.closest('.rsd-row');
          const card = heartBtn.closest('.rsd-card');
          if (row) row.classList.toggle('rsd-row--picked', picks.has(key));
          if (card) card.classList.toggle('rsd-card--picked', picks.has(key));
        });
      }
    }

    /**
     * Update the picks count badge
     */
    function updatePicksCount() {
      if (!elements.picksCount) return;
      const count = picks.size;
      elements.picksCount.textContent = count;
      elements.picksCount.style.display = count === 0 ? 'none' : 'inline-flex';
    }

    /**
     * Escape HTML to prevent XSS
     */
    function escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    /**
     * Escape string for safe use in HTML attributes
     */
    function escapeAttr(str) {
      if (!str) return '';
      return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * Debounce function
     */
    function debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }
})();

